import { Measure } from '@wizeoo/akm-message-lib';
import { ViewData } from '../../model/view.data';
import { FreedataArgsVO } from '../../vo/core/freedataargs.vo';
import { FreedataValueVO } from  '../../vo/core/freedatavalue.vo';
import { FunctionObj, FunctionCoreEngine, MathUtils, ComputeResult, WizConstants, MeasureVO, MeasMode, MeasModeItem, MeasureCoreEngine } from '@wizeoo/akm-compute-lib';
import { Model } from '../../model/model';
import { FeiVO } from '../../vo/workingdata/fei.vo';

/** 
 * objet de déclenchement de calcul, pour ordonnancement dans formWidget
 * permet de jouer les calculs par dépendances
 */
export interface TriggerMatrixItem { 
  code: string; // le code de l'args
  fdavo: FreedataArgsVO; // l'args
  children: TriggerMatrixItem[]; // les dépendances pour recalcul (triggers)
}

/**
 * calculs des champs de freedatas
 */
export class FreedataFormulaCoreEngine {
  
  protected model: Model = null;
  constructor(model: Model) {
    this.model = model;
  }


  /**
   * construction de la matrice de dépendances de calculs sur les FDVs (FdFormWidget)
   * @param fieldMap la map d'args
   * @returns la matrice de dépendances
   */
  public buildTriggerMatrix(fieldsMap: Map<string, FreedataArgsVO>, fei: FeiVO): Map<string, TriggerMatrixItem> {

    // les args déjà identifiés
    const hitMap: Map<string, TriggerMatrixItem> = new Map();
    // les args en cours de résolution. Doit être vide en fin de traitement
    let pendingMap: Map<string, FreedataArgsVO> = new Map(fieldsMap);

    // éviter les boucles infinies. Si le pending ne change pas de taille, y'a un problème
    let thisTimeSize: number = pendingMap.size;
    let exitOnError: boolean = false;
    while(pendingMap.size > 0 && !exitOnError) { // on va dépiler la map, par niveaux de dépendances
      [...pendingMap.keys()].forEach((key: string) => { // pour chaque champ
        const fdavo: FreedataArgsVO = pendingMap.get(key);
        
        // le triggerItem
        const trig: TriggerMatrixItem = {code: fdavo.code, fdavo, children: []};
  
        if(!fdavo.valorTrigger) { // pas de déclencheur, niveau 0
          hitMap.set(fdavo.code, trig); // ajout à la map des trouvés
          pendingMap.delete(fdavo.code); // on supprime de la map
          
        } else { // champs déclencheurs. On vérifie que toutes les dépendances sont dispo
          const triggers: string[] = fdavo.valorTrigger.split(ViewData.KEY_SEPARATOR);
          
          // PATCH gestion de l'injection de valeur dans un autre champ (Num vers shortCode)
          let injectMethod: boolean = false;
          // ajout du trigger self pour inject
          triggers.forEach((key: string) => {
            if(key === fdavo.code  && fdavo.valor.indexOf(FreedataFormulaCoreEngine.INJECT) > -1) { // !!key en cas de mauvais pipe // && key !== fdavo.code
              // console.log('triggers self', fdavo);
              injectMethod = true;
            }
          });
          

          if(injectMethod) { // injection de valeur
            // console.log('injectMethod', trig);
            hitMap.set(fdavo.code, trig); // ajout à la map des trouvés
            hitMap.get(fdavo.code).children.push(trig);
            pendingMap.delete(fdavo.code); // on supprime de la map

          } else {
            let allParentsIn: boolean = true; // contrôle: toutes les dépendances doivent être dispo
            triggers.forEach((key: string) => {
              if(!!key && !hitMap.has(key) ) { // !!key en cas de mauvais pipe // && key !== fdavo.code
                allParentsIn = false;
              }
            });

            if(allParentsIn) { // toutes les dépendances sont dispos, je m'ajoute
              triggers.forEach((key: string) => { // ajout comme enfant sur chaque champ
                if(!!key) {
                  hitMap.get(key).children.push(trig);
                }
              });
              hitMap.set(fdavo.code, trig); // ajout à la map des trouvés
              pendingMap.delete(fdavo.code); // on supprime de la map
            }
          }
        }
      });

      if(pendingMap.size === thisTimeSize && pendingMap.size > 0) {
        // console.log('oups FD deps error', pendingMap);
        exitOnError = true;
        pendingMap.clear();
      }

      thisTimeSize = pendingMap.size; // mise à jour de l'indicateur de pile

    }
    // console.log('hitmap', hitMap);
    return hitMap;
  }
  
  
  // ######################################
  // refonte, gestion de la récursivité des ()
  // ######################################

  public static MANDATORY: string = 'MANDATORY'; // clé obligatoire sur FD
  // public static UNIQUE: string = 'UNIQUE'; // clé unique sur FD
  public static INJECT: string = 'INJECT'; // injection d'un champ dans un autre sur trigger

  /** le pattern pour détection des variables externes (sous la forme FD#Mfield) */
  public static NEO_MM_PATTERN: RegExp = /[a-zA-Z]{1}[a-zA-Z0-9_-]+#[a-zA-Z0-9_]*[a-zA-Z0-9]{1}/;

  // recherche d'un pattern MM au global dans la formule pour lister les dépendances
  public static NEO_GLOBAL_MM_PATTERN: RegExp = new RegExp(FreedataFormulaCoreEngine.NEO_MM_PATTERN.source, 'g');

  // TODO extraire les chaines entre "" au 1er round

  // recherche des éléments unitaires
  public static SECOND_ROUND_PATTERN: RegExp = new RegExp(
    MeasureCoreEngine.NEO_TEXT_PATTERN.source +
      '|' +
      MeasureCoreEngine.NEO_SUB_PATTERN.source +
      '|' +
      MeasureCoreEngine.NEO_FIRST_EQUAL_PATTERN.source +
      '|' +
      FreedataFormulaCoreEngine.NEO_MM_PATTERN.source +
      '|' +
      MeasureCoreEngine.NEO_OPERATOR_PATTERN.source +
      '|' +
      MeasureCoreEngine.NEO_COMPARATOR_PATTERN.source +
      '|' +
      MeasureCoreEngine.NEO_NUMBER_PATTERN.source,
    'g'
  ); 

  /**
   * demande de calcul d'une mesure donnée. Va calculer d'abord toutes les dépendances
   * @param prototype 
   * @param fdv 
   * @returns 
   */
  public fastComputeFreedataMM(prototype: MeasMode, fdv: FreedataValueVO, fei: FeiVO): ComputeResult {
    return this.computeMmiDependencies(prototype.mmi, fdv, fei);
  }

  /**
   * création d'un MeasMode (composition sur Measure avec #Mode (depuis Constant.MEASURE_MODE_xxx))
   *
   * @param measure La mesure concernée
   * @param mode    Mode à utiliser
   * @return MeasMode MeasMode créé
   */
  public measureModeFromFormula(measure: MeasureVO, mode: string): MeasMode {
    const result: MeasMode = 
    { 
      code: [measure.shortCode, mode].join(WizConstants.CONTEXT_SEPARATOR), // mc#mm
      form: measure['formula' + mode], // formule
      m: measure.source,
      mc: measure.shortCode, // code mesure
      mm: mode, // mode mesure
      mmi: null,
      r: null // result
    };

    // collecte des dépendances sur d'autres mesures
    let cleanFormula: string = result.form ? result.form : '';

    // on enlève toutes les chaines entre ""
    cleanFormula = cleanFormula.replace(MeasureCoreEngine.NEO_TEXT_PATTERN, '');
    // console.log('clean formula', result.formula, cleanFormula);

    const cleanFormulaParts = cleanFormula.match(FreedataFormulaCoreEngine.NEO_GLOBAL_MM_PATTERN);
    if (cleanFormulaParts) {
      cleanFormulaParts.forEach((group: string) => {
        if (!result.deps) {
          result.deps = new Set();
        }
        result.deps.add(group);
      });
    }

    // création du MMI qui contiendra la pile de calcul pour traitement récursif
    result.mmi = this.buildMeasureModeItem(result, result.form);
    // console.log('mmf', result.formula, result.result);
    return result;
  }

  /**
   * Création d'une étape de calcul dans un MeasMode le MMI contient une pile de mmi pour ventilation du calcul en éléments simple. A calculer en
   * récursif
   *
   * @param measureMode MeasMode à calculer
   * @param rawFormula  la formule "brute" à analyser. Elle sera finale (le mmi sera un opérateur ou une opérande) ou pas (formule, parenthèses)
   * @return MeasModeItem Etape de calcul créée
   */
  public buildMeasureModeItem(mm: MeasMode, form: string): MeasModeItem {
    const result: MeasModeItem =
    {
      mm, // mesureMode porteur
      form, // formule brute
      r: null,  // result
      c: [] // sous-collection de mmi
    }


    // construction de la map des éléments gigognes (parenthèses et fonctions)
    // sous la forme  {x} = [la formule (éventuellement transformée avec de {{x}})forcément simple, la vraie formule, le mmi correspondant, créer au 2ème step]
    const partsMaps: Map<number, [string, string, MeasModeItem]> = new Map<
      number,
      [string, string, MeasModeItem]
    >();
    let idx: number = 0;
    let workingFormula: string = form; // on clone la formule brute, pour remplacer les éléments complexes par des {{x}}
    let rawFormulaArr: string[] = [];
    if (workingFormula) {
      // ### 1er traitement, remplacement des éléments à parenthèse ou fonction dans le partsMaps
      rawFormulaArr = workingFormula.match(MeasureCoreEngine.FIRST_ROUND_PATTERN);
      while (rawFormulaArr && rawFormulaArr.length > 0) {
        rawFormulaArr.forEach((group: string) => {
          idx++;
          // recherche des {{x}} dans le groupe (possible dès le 2ème passage, pour préservation de la vraie formule)
          const innerAccols: string[] = group.match(MeasureCoreEngine.INNER_SUB_PATTERN);
          let realFormula: string = group;
          if (innerAccols && innerAccols.length > 0) {
            // la sous formule contient une sous-formule. On alimente correctement sur "formule d'origine"
            innerAccols.forEach((innerGroup: string) => {
              const innerIdx = innerGroup.substring(2, innerGroup.length - 2);
              // console.log('inner', innerGroup, innerIdx);
              realFormula = realFormula.replace(innerGroup, partsMaps.get(Number(innerIdx))[1]); // et remplacement de la chaine correspondante dans la formule en cours
            });
          }

          partsMaps.set(idx, [group, realFormula, null]); // ajout de l'entrée dans le dico de sous-éléments
          workingFormula = workingFormula.replace(group, '{{' + idx + '}}'); // et remplacement de la chaine correspondante dans la formule en cours

          rawFormulaArr = workingFormula.match(MeasureCoreEngine.FIRST_ROUND_PATTERN); // et rebelote. les (()) sont devenus des ({{x}})
        });
      }

      // alimentation des MMI de la partsMaps pour futur remplacement dans les contents
      let subMMI: MeasModeItem;
      if (partsMaps.size > 0) {
        // s'il existe des sous-éléments, on créer leurs MMis
        // console.log ('FD fce parts', workingFormula, partsMaps, idx);

        let toBuildIdx: number = 1; // on va les construire dans l'ordre
        while (toBuildIdx <= idx) {
          const subWorkingFormula = partsMaps.get(toBuildIdx)[0]; // collecte de la sous-formule transformée
          const subRealFormula = partsMaps.get(toBuildIdx)[1]; // collecte de la sous-formule. C'est soit () soit Func()
          subMMI = this.buildRawMMI(mm, subRealFormula);

          // if (measureMode.code ==="MES_AAK#I") {
          //   console.log('aak', measureMode);
          // }

          if (MeasureCoreEngine.NEO_FUNCTION_PATTERN.test(subWorkingFormula)) {
            // c'est soit une formule
            subMMI.fFunc = true;
            subMMI.funcCode = subWorkingFormula.split(/\(.*\)/)[0];
            FunctionCoreEngine.checkFunctionExist(this.model, subMMI); // contrôle de validité & type (aggrégat)
            if(!subMMI.isErr) {
              // construction des enfants
              this.buildSubMMI(subMMI, subWorkingFormula, partsMaps);
            }
          } else if (MeasureCoreEngine.NEO_PARENTHESIS_PATTERN.test(subRealFormula)) {
            // soit une parenthèse
            subMMI.fSis = true;
            this.buildSubMMI(subMMI, subWorkingFormula, partsMaps);
          } else {
            subMMI.isErr = true;
            // console.error('unknown subPart');
          }

          partsMaps.get(toBuildIdx)[2] = subMMI;
          toBuildIdx++; // pour ajout du mmi (et de ses enfants...) dans partsMMiMaps
        }
      }

      // suppression du 1er =
      workingFormula = workingFormula.replace(MeasureCoreEngine.NEO_FIRST_EQUAL_PATTERN, '');
      // if(workingFormula.match(this.NEO_FIRST_EQUAL_PATTERN))

      // if (workingFormula === '{{2}}') {
      //   console.log('oups', workingFormula, partsMaps);
      // }
      this.buildSubMMI(result, workingFormula, partsMaps); // construction du mmi principal
    }
    return result;
  }

  /**
   * contrôle de cohérence des paramètres
   * alternance d'opérateur / opérandes
   * existance des mesures. Pas de mesure 'self' dans la mesure
   * TODO contrôle des paramètres des fonctions
   * @param mmi le mesureModeItem
   * @param partsMaps les sous-éléments
   */
  public checkIntegrity(mmi: MeasModeItem, partsMaps: Map<number, [string, string, MeasModeItem]>): void {
    // controle de cohérence. Les groups doivent reconstituer la chaine complète
    // peu importe les espaces, le moteur sait gérer
    const rawNoSpace: string = mmi.form.replace(/\s+/g, '').replace(MeasureCoreEngine.NEO_FIRST_EQUAL_PATTERN, '');
    let partJoin: string = '';
    if (mmi.c) {
      if (mmi.fFunc) {
        partJoin = mmi.c.map((subMMi: MeasModeItem) => subMMi.form).join(';');
      } else {
        partJoin = mmi.c.map((subMMi: MeasModeItem) => subMMi.form).join('');
      }
    }
    if (mmi.fSis || mmi.fFunc) {
      partJoin = '(' + partJoin + ')';
    }
    if (mmi.fFunc) {
      partJoin = mmi.funcCode + partJoin;
    }

    partJoin = partJoin.replace(/\s+/g, '');
    if (rawNoSpace !== partJoin) {
      // console.log('Unknown part Error', rawNoSpace, partJoin);
      mmi.isErr = true;
      mmi.errC = FunctionCoreEngine.UNKNOW_PART_ERROR;
      mmi.mm.isErr = true;
      mmi.mm.errC = FunctionCoreEngine.UNKNOW_PART_ERROR;
      mmi.mm.errS = mmi.form;
      // console.log('Unknown part Error', mmi.c, mmi.form);
    }

    // controle de l'alternance des operateurs / operandes
    if (mmi.form && mmi.c && !mmi.fFunc) {
      // pas pour les fonctions. On va les contrôles spécifiquement
      let currentKindSign: number = 0; // on va passer 1 pour les opérateurs, -1 pour le reste (tout. le reste retourne une valeur)
      // si on a 2x le même à la suite, problème d'enchainement
      // if (mmi.rawFormula === '=+20-(-45*2)+(-BOB#I)(12)') {
      //   console.log('me');
      // }

      // une parenthèse avec un seul enfant de type opérateur / comparateur
      if (
        mmi.fSis &&
        mmi.c[0].c.length === 1 &&
        (mmi.c[0].c[0].fOper || mmi.c[0].c[0].fComp)
      ) {
        console.log('OP alone Error', mmi.c, mmi.form);
        mmi.isErr = true;
        mmi.r = FunctionCoreEngine.OPERATOR_ERROR;
        mmi.errC = FunctionCoreEngine.OPERATOR_ERROR;
        mmi.mm.isErr = true;
        mmi.mm.errC = FunctionCoreEngine.OPERATOR_ERROR;
        mmi.mm.errS = mmi.form;
      }

      mmi.c.forEach((subMMi: MeasModeItem) => {
        // console.log('check', mmi.rawFormula, mmi.content, subMMi);
        if (subMMi.fOper || subMMi.fComp) {
          if (currentKindSign === 1) {
            console.log('OP alternate Error', mmi.c, mmi.form);
            mmi.isErr = true;
            mmi.r = FunctionCoreEngine.OPERATOR_ERROR;
            mmi.errC = FunctionCoreEngine.OPERATOR_ERROR;
            mmi.mm.isErr = true;
            mmi.mm.errC = FunctionCoreEngine.OPERATOR_ERROR;
            mmi.mm.errS = mmi.form;
          }
          currentKindSign = 1;
        } else {
          if (subMMi.fOper && subMMi.form === mmi.mm.code) {
            // measure self dans la mesure
            // ByPass INHERIT, SPLIT et COLLECT
            // if (  mmi.measureMode.formula.indexOf('INHERIT') > 0 ||
            //       mmi.measureMode.formula.indexOf('SPLIT') > 0 ||
            //       mmi.measureMode.formula.indexOf('COLLECT') > 0) {
            //   // améliorer le contrôle en balayant récursivement les contexts
            // } else {
            console.log('SELF Error', mmi.c, subMMi);
            mmi.isErr = true;
            mmi.r = FunctionCoreEngine.CIRCULAR_ERROR;
            mmi.errC = FunctionCoreEngine.CIRCULAR_ERROR;
            mmi.mm.isErr = true;
            mmi.mm.errC = FunctionCoreEngine.CIRCULAR_ERROR;
            mmi.mm.errS = mmi.form;
            // }
          }

          if (currentKindSign === -1) {
            console.log('VAL Error', mmi.c, subMMi);
            mmi.isErr = true;
            mmi.r = FunctionCoreEngine.VALUE_ERROR;
            mmi.errC = FunctionCoreEngine.VALUE_ERROR;
            mmi.mm.isErr = true;
            mmi.mm.errC = FunctionCoreEngine.VALUE_ERROR;
            mmi.mm.errS = mmi.form;
          }
          currentKindSign = -1;
        }
      });

      if (currentKindSign === 1) {
        // termine par un opérateur / comparateur, anormal
        console.log('OP ending Error', mmi.c, mmi.form);
        mmi.isErr = true;
        mmi.r = FunctionCoreEngine.OPERATOR_ERROR;
        mmi.errC = FunctionCoreEngine.OPERATOR_ERROR;
        mmi.mm.isErr = true;
        mmi.mm.errC = FunctionCoreEngine.OPERATOR_ERROR;
        mmi.mm.errS = mmi.form;
      }
    }
  }

  /**
   * alimentation des mmis enfant d'un MMI (soit global, soit issus d'une () ou d'une funct())
   * @param mmi à compléter
   * @param workingFormula sa formule "brute" simplifiée (pas de gigognes, des {{x}})
   * @param parts le tableau d'éléments gigognes
   */
  public buildSubMMI(
    mmi: MeasModeItem,
    workingFormula: string,
    parts: Map<number, [string, string, MeasModeItem]>
  ): void {
    let child: MeasModeItem;
    // 3 cas possibles: fonction, () ou formule "simple"
    if (mmi.fFunc) {
      const param: string = workingFormula.split(/^[A-Z]*\(|\)$/)[1]; // extraction des params
      let params: string[] = [];
      if (param && param.length > 0) {
        params = param.split(FunctionCoreEngine.FUNCTION_SEPARATOR);
        // console.log('FDV fce functionSubPattern', params);
        let paramIdx: number = 1;
        params.forEach((group: string) => {
          child = this.buildRawMMI(mmi.mm, group); // attention, le group est peut-être un {{x}}, à remplacer...
          if (!group || group.length === 0) {
            mmi.isErr = true;
            mmi.mm.isErr = true;
            mmi.r = FunctionCoreEngine.EMPTY_FUNCTION_PARAM_ERROR;
            mmi.errC = FunctionCoreEngine.EMPTY_FUNCTION_PARAM_ERROR;
            mmi.mm.errC = FunctionCoreEngine.EMPTY_FUNCTION_PARAM_ERROR;
            console.log('empty param Error', mmi.c, mmi.form);
            mmi.mm.errS = paramIdx.toString();
          } else {
            this.buildSubMMI(child, group, parts);
            if (child.c && child.c.length > 0) {
              child.form = child.c.map((item) => item.form).join(''); // fix mauvais remplacement des {{x}} sur Func(Func(Measure))
            }
          }
          mmi.c.push(child);
          paramIdx++;
        });
      }
      // contrôle du bon nombre de params
      const func: FunctionObj = FunctionCoreEngine.functionMap(this.model).get(mmi.funcCode);
      if (!func.checkParamsLength(params.length)) {
        mmi.isErr = true;
        mmi.mm.isErr = true;
        mmi.r = FunctionCoreEngine.MISMATCH_FUNCTION_PARAM_ERROR;
        mmi.errC = FunctionCoreEngine.MISMATCH_FUNCTION_PARAM_ERROR;
        mmi.mm.errC = FunctionCoreEngine.MISMATCH_FUNCTION_PARAM_ERROR;
        console.log('mismatch param Error', mmi.c, mmi.form);
        mmi.mm.errS = params.length + '/' + func.parameters.length;
      }
    } else if (mmi.fSis) {
      // construction d'un enfant sans les parenthèses
      child = this.buildRawMMI(mmi.mm, mmi.form.substring(1, mmi.form.length - 1));
      this.buildSubMMI(child, workingFormula.substring(1, workingFormula.length - 1), parts);
      mmi.c.push(child);
    } else {
      // une sous-chaine classique
      const groups: string[] = workingFormula.match(FreedataFormulaCoreEngine.SECOND_ROUND_PATTERN);
      // console.log('subPattern', groups);
      if (groups && groups.length > 0) {
        groups.forEach((group: string) => {
          child = this.buildRawMMI(mmi.mm, group); // attention, le group est peut-être un {{x}}, à remplacer...
          this.neoDefineMeasureModeItem(child, group, parts);
          // ici, si l'enfant est = {{x}}, remplacement dans le workingFormula
          if (MeasureCoreEngine.NEO_SUB_PATTERN.test(group)) {
            const innerIdx = group.substring(2, group.length - 2);
            mmi.form = mmi.form.replace(group, parts.get(Number(innerIdx))[1]);
          }

          mmi.c.push(child);
        });
      }
    }
    // contrôle de cohérence de la chaine. Les opérateurs doivent alterner avec les valeur
    this.checkIntegrity(mmi, parts);
  }

  /**
   * construction d'un mmi brut
   * @param mm le mm source
   * @param form la formule (vraie)
   */
  public buildRawMMI(mm: MeasMode, form: string): MeasModeItem {
    const result: MeasModeItem = 
    {
      c: [], // sous-contenu de calcul
      mm, // mesureMode porteur
      form, // formule brute
      r: null // result
    }
    return result;
  }

  /**
   * Construction d'une étape de calcul "simple"
   * enfant) Si la formule contient une fonction, on va découper plus finement Si la formule contient une fonction inconnue, on bascule en erreur
   *
   * @param mmi à calculer
   * @param rawFormula  La formule à analyser
   * @param parts, les {{x}}
   */
  private neoDefineMeasureModeItem( result: MeasModeItem, rawFormula: string, parts: Map<number, [string, string, MeasModeItem]>
  ): void {
    if (MeasureCoreEngine.NEO_SUB_PATTERN.test(rawFormula)) {
      // on reconnait un {{x}}, on remplace le rawFormula et on lui passe le contenu du parts
      const innerIdx = rawFormula.substring(2, rawFormula.length - 2);

      result.fFunc = parts.get(Number(innerIdx))[2].fFunc;
      result.fSis = parts.get(Number(innerIdx))[2].fSis;
      result.funcCode = parts.get(Number(innerIdx))[2].funcCode;
      result.form = parts.get(Number(innerIdx))[1]; // et remplacement de la chaine correspondante dans la formule en cours
      result.c = parts.get(Number(innerIdx))[2].c; // et prise des enfants du mmi
    } else if (MeasureCoreEngine.NEO_TEXT_PATTERN.test(rawFormula)) {
      // un texte pour affichage direct
      // console.log('TEXT_PATTERN', rawFormula);
      // result = rawFormula; // TODO suppression des ""
      result.r = rawFormula.replace(MeasureCoreEngine.OUTERQUOTES, '');
      result.ftxt = true;
      result.fTer = true;
    }
    else if (FreedataFormulaCoreEngine.NEO_MM_PATTERN.test(rawFormula)) {
      // une variable de type MEASURE#Mode
      const partMeasure: string = rawFormula.split(WizConstants.CONTEXT_SEPARATOR)[0];
      const partMode: string = rawFormula.split(WizConstants.CONTEXT_SEPARATOR)[1];
      result.fRand = true;
      result.fTer = true;
    } else if (MeasureCoreEngine.NEO_OPERATOR_PATTERN.test(rawFormula)) {
      // on reconnait un opérateur +-/*
      // console.log('OPERATOR_PATTERN', rawFormula);
      result.fOper = true;
      result.fTer = true;
    } else if (MeasureCoreEngine.NEO_COMPARATOR_PATTERN.test(rawFormula)) {
      // on reconnait un comparateur <=>
      // console.log('COMPARATOR_PATTERN', rawFormula);
      result.fComp = true;
      result.fTer = true;
    }  else if (MeasureCoreEngine.NEO_NUMBER_PATTERN.test(rawFormula)) {
      // un nombre
      result.fConst = true;
      result.fTer = true;
    } else {
      // le fragment égale toute la rawFormula, mais ne correspond à rien ???
      // console.log('TEXT_PATTERN de rien', rawFormula);
      result.ftxt = true;
      result.fTer = true;
      result.isErr = true;
      result.errC = FunctionCoreEngine.UNKNOW_PART_ERROR;
      result.mm.isErr = true;
      result.mm.errC = FunctionCoreEngine.UNKNOW_PART_ERROR;
      console.log('Unknown part Error', result.c, result.form);
      result.mm.errS = result.form;
    }
  }

  /**
   * Calcul récursif des mmi du mmi principal du MeasMode
   *
   * @param mmi      Etape de calcul à traiter
   * @param baseline Ligne de travail
   * @param fei      Instance du moteur de calcul
   */
  private computeMmiDependencies(mmi: MeasModeItem, fdv: FreedataValueVO, fei: FeiVO): ComputeResult {
      // tout sauf les fonctions collect
      // console.log('other', mmi, mmi.functionCode, mmi.rawFormula, mmi.content);
      if (mmi.c.length > 0) {
        // des paramètres, calcul à effectuer

        mmi.nbComp = 0; // reset du nb comparator identifiés

        mmi.c.forEach((child: MeasModeItem) => {
          // on calcule tous les enfants
          this.computeMmiChild(mmi, fdv, child, fei);
        }); // fin du for, tous les contents ont un résult

        // ### Phase calcul
        if (mmi.fFunc) {
          // fonction
          // console.log('func', mmi.functionCode, mmi, mmi.collectContent);
          // TODO calculer les params
          FunctionCoreEngine.callFunction(mmi.funcCode, mmi, fdv, fei); // on inject le FDV à la place de la widgetLine (INJECT)
        } else {
          // calcul simple
          // présence de comparateur
          if (mmi.nbComp <= 1) {
            // simple Math(0) ou comparaison(1), retour 1 ou 0 (Bool)
            MathUtils.computeMathParam(mmi);
            // console.log('computemathparam', mmi.content );
          } // plus de 1 comparateur, erreur
          else {
            mmi.isErr = true;
            mmi.r = FunctionCoreEngine.FUNCTION_ERROR;
          }
        }
      } else {
        // pas d'enfants ? je suis terminal / une constante / un texte

        if (mmi.fConst) {
          // numérique
          mmi.r = mmi.form;
        } else if (mmi.ftxt) {
          // texte, on enlève les quotes
          mmi.r = mmi.form.replace(MeasureCoreEngine.OUTERQUOTES, '');
          // mmi.setResult(mmi.getRawFormula());
        } else if (mmi.fRand) {
          // je fais référence à un autre champ
          const cr: ComputeResult = this.getFdvValue(fdv, mmi.form); //  fdv.argsObjVO[mmi.rawFormula]; // TODO splitter sur #[1]
          mmi.r = cr.valor;
        } else {
          // je suis une formule S sans valeur, on doit jamais passer ici
          //mmi.result = baseline.mms[mmi.measureMode.code]? baseline.mms[mmi.measureMode.code].result : null;
          // console.log('S me', baseline.mms[mmi.measureMode.code] );
        }
      }

      return {
        valor: mmi.r,
        error: mmi.isErr
      }
  }

  /**
   * Calcul d'une étape de calcul "enfant" Récursivité dans le cas d'une étape "fonction" ou "parenthèses"
   *
   * @param mmi      Etape de calcul "parent"
   * @param baseline Ligne de travail concernée
   * @param child    Etape de calcul "enfant" à calculer
   * @param fei      Instance de gestion des formules
   */
  private computeMmiChild(mmi: MeasModeItem, fdv: FreedataValueVO, child: MeasModeItem, fei: FeiVO): void {
    if (child.fTer) {
      // calcul terminé pour l'enfant
      if (child.fConst) {
        // nombre
        child.r = child.form;
      } else if (child.ftxt) {
        // texte, suppression des quotes
        child.r = child.form.replace(MeasureCoreEngine.OUTERQUOTES, '');
      } else if (child.fRand) {
        // variable (un autre champ)
          child.r = this.getFdvValue(fdv, child.form).valor; 
      } else if (child.fComp) {
        // comparateur, on incrémente le compteur sur le parent (1 seul sera autorisé)
        mmi.nbComp = mmi.nbComp + 1;
      } else if (!child.fOper) {
        // ici, il ne doit rester que les opérateurs, qui seront gérés lors du calcul global
        // donc on doit pas passer ici...
        console.warn('unknow terminal part: {}', mmi.form);
      }
    } else {
      // pas terminal. C'est une fonction ou des parenthèses
      // Fonction ou parenthèses
      this.computeMmiChildFunctionOrParenthesis(mmi, fdv, child, fei);
    }
  }

  /**
   * Calcul d'une étape de calcul fonction ou parenthèses Récursivité dans ce cas
   *
   * @param mmi      Etape de calcul "parent"
   * @param baseline Ligne de travail concernée
   * @param child    Etape de calcul "enfant" à calculer
   * @param fei      Instance de gestion des formules
   */
  private computeMmiChildFunctionOrParenthesis(
    mmi: MeasModeItem,
    fdv: FreedataValueVO,
    child: MeasModeItem, fei: FeiVO ): void {
    if (child.fSis) {
      // parenthèses, calcul des enfants de l'enfant unique
      const childMeasureModeItem: MeasModeItem = child.c[0];
      // dans le cas des parenthèses, l'enfant contient la formule sans parenthèses
      this.computeMmiDependencies(childMeasureModeItem, fdv, fei);
      // donc prise du résultat de l'enfant
      child.r = childMeasureModeItem.r;

      if (childMeasureModeItem.isErr) {
        // ici. Si variable n'existe pas
        // console.error('Missing children for calculation on parenthesis');
        child.isErr = true;
        mmi.isErr = true;
        mmi.r = childMeasureModeItem.r;
      }

      // if (mmi.rawFormula) {
      //   baseline.mmiMap.set(mmi.rawFormula, mmi.result);
      //   // console.log('mmifuncOrparent', mmi.rawFormula, mmi.result);
      // }
    } else if (child.fFunc) {
      // fonction
      // calcul des enfants (paramètres de la fonction) . La fonction sera calculée lors de la phase...calcul
      // TODO FormulaCoreEngine.computeMmiDependencies(child, baseline, fei);
      // console.log('FDV Formula FUNC', child, child.functionCode);
      this.computeMmiDependencies(child, fdv, fei);
    } else {
      // rien ? je suis un paramètre de fonction
      // TODO FormulaCoreEngine.computeMmiDependencies(child, baseline, fei);
      this.computeMmiDependencies(child, fdv, fei);
    }
  }


  /**
   * retourne la valeur d'un champ de FD sous la forme CODEDECHAMP#codeField
   * CODEDECHAMP#SELF pour prise de la valeur directe
   * @param fdv le FreedataValue
   * @param fdField NOMDUCHAMP (sur le FD)#codeDuField
   * @returns 
   */
  private getFdvValue(fdv: FreedataValueVO, fdField: string): ComputeResult {
    
    
    

    const parts: string[] = fdField.split(ViewData.CONTEXT_SEPARATOR);
    const fieldPart: string = parts[1];
    const rawVal = fdv.argsObjVO[parts[0]] || null;
    

    let result: string = rawVal;
    
    // console.log('getFDValue',fdv, fdField, rawVal);

    // on veut un sous-champ
    if(!!rawVal && fieldPart !== 'SELF') {
      const rawValSplit: string[] = rawVal.split(ViewData.CONTEXT_SEPARATOR);
      const subFDV: FreedataValueVO = this.model.freedataEngine.getFreedataValue(rawValSplit[0], rawValSplit[1]);
      result = subFDV?.argsObjVO[fieldPart] || null;
    }
    
    return {valor: result, error: false} ;
  }

  /* #############################################################
          calculs sur FD / FDV
     ############################################################# */



  /**
   * TODO, améliorer
   * Gestion des restrictions de saisie : Maxchar
   * @param fdv cible
   * @param exp formule
   * @returns true / false
   */
  public checkMaxChar(fdv: FreedataValueVO, exp: string, fei: FeiVO): boolean {
    let result: boolean = false;
    const cr = this.computeFreedataExp(fdv, exp, fei);
    if ( cr.valor !== '0') {
      result = true;
    } 
    return result;
  }

  /**
   * pour une expression (valid / restrict), retourne true / false selon le calcul
   * @param fdv cible
   * @param exp formule
   * @returns true / false
   */
  public checkFreedataExp(fdv: FreedataValueVO, exp: string, fei: FeiVO): boolean {
    let result: boolean = false;
    const cr = this.computeFreedataExp(fdv, exp, fei);
    // if(exp === 'OR(EMPTY(FLUX#code);FLUX#BOOKED=1)') {
    //   console.log('checkFreedataExp', fdv.shortCode, fdv.argsObjVO);
    // }
    if ( cr.valor !== '0' ) {
      result = true;
    } 
    return result;
  }

  /**
   * calcul d'une expression pour une valeur de FDV
   * retourne le measureMode
   * @param fdv cible
   * @param exp expression à calculer
   * @returns 
   */
  public computeFreedataExp(fdv: FreedataValueVO, exp: string, fei: FeiVO): ComputeResult {
    
    const meas: MeasureVO = new MeasureVO(new Measure());
    meas.shortCode = 'checkFDFormula';
    meas.code = meas.shortCode;
    meas.mode = WizConstants.MEASURE_MODE_SUBMIT;
    meas.formulaS = '=' + exp;
    const mm: MeasMode = this.measureModeFromFormula(meas, meas.mode);

    // calcul
    let result: ComputeResult = this.fastComputeFreedataMM(mm, fdv, fei);
    return result;
  }


}